import React from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  mergeProposalSubjectsWithImages,
  currentSessionModeAtom,
  selectedSubjectAtom,
  intraIdReviewStatusFilterValueAtom,
  useRefreshMergeProposalSubjectsIfExpired
} from "../state";
import ImageLane from "./ImageLane";
import { ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { useSessionApiClient } from "../api/sessions";
import { FINALIZED_REVIEW_STATUS, NEEDS_REVIEW_STATUS } from "../constants";
import timestampToHuman from "../utils/date";
import { useEffect } from "react";

const LaneControls = ({ mergeProposal, mergeProposalSubject, onChange }) => {
  const handleChange = (evt, newMergeState) => {
    if (newMergeState && mergeProposalSubject.mergeState !== newMergeState) {
      onChange(mergeProposalSubject, newMergeState);
    }
  };

  const merged = mergeProposalSubject.mergeState === "merged";
  const finalized = mergeProposal.review_status === FINALIZED_REVIEW_STATUS;

  return (
    <ToggleButtonGroup
      value={merged ? "merge" : mergeProposalSubject.mergeState}
      exclusive
      color={merged ? "standard" : "info"}
      onChange={handleChange}
      disabled={finalized || merged}
      data-testid={`toggleButtonGroup-${mergeProposalSubject.subjectName}`}
    >
      <ToggleButton
        value="merge"
        disabled={finalized || mergeProposalSubject.mergeState === "merge"}
      >
        <Typography>Merge</Typography>
      </ToggleButton>
      <ToggleButton
        value="no-merge"
        disabled={finalized || mergeProposalSubject.mergeState === "no-merge"}
      >
        <Typography>No Merge</Typography>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

const MergeProposalCleaningArea = ({ mergeProposal, sessionId }) => {
  const refreshIfExpired = useRefreshMergeProposalSubjectsIfExpired({ mergeProposal, sessionId });

  const [mergeProposalSubjects, setMergeProposalSubjects] = useRecoilState(
    mergeProposalSubjectsWithImages({ mergeProposal, sessionId })
  );
  const setIntraIdReviewStatusFilterValue = useSetRecoilState(intraIdReviewStatusFilterValueAtom);
  const setSelectedSubject = useSetRecoilState(selectedSubjectAtom);
  const setCurrentSessionMode = useSetRecoilState(currentSessionModeAtom);
  const apiClient = useSessionApiClient();

  const handleMergeStateChange = async (
    mergeProposalSubject,
    newMergeState
  ) => {
    const stateUpdate = await apiClient.updateMergeProposalSubjectState(
      sessionId,
      mergeProposalSubject,
      newMergeState
    );
    setMergeProposalSubjects(
      mergeProposalSubjects.map((item) =>
        item.subjectName === mergeProposalSubject.subjectName
          ? {
            ...mergeProposalSubject,
            mergeState: stateUpdate.status,
          }
          : item
      )
    );
  };

  const navigateToIntraIdSubject = async (subjectName) => {
    const subject = await apiClient.getSubject(sessionId, subjectName);
    const newReviewStatusTab = subject.review_status;
    setIntraIdReviewStatusFilterValue(newReviewStatusTab);
    setCurrentSessionMode("intra");
    setSelectedSubject({ name: subjectName });
  }

  const laneTitle = (subjectName) => {
    return `Subject: ${subjectName}`;
  };

  const subtitle = () => {
    if (mergeProposal.review_status !== NEEDS_REVIEW_STATUS) {
      return `Finalized by ${mergeProposal.last_review_by
        } on ${timestampToHuman(mergeProposal.last_review_at)}`;
    }
    return null;
  };

  useEffect(() => {
    refreshIfExpired();
  });

  return (
    <>
      {mergeProposalSubjects.length > 0 &&
        mergeProposalSubjects.map((mergeProposalSubject) => (
          <ImageLane
            key={mergeProposalSubject.subjectName}
            images={mergeProposalSubject.images}
            laneTitle={laneTitle(mergeProposalSubject.subjectName)}
            subtitle={subtitle()}
            totalImages={mergeProposalSubject.images.length}
            titleInteraction={() => navigateToIntraIdSubject(mergeProposalSubject.subjectName)}
            readOnly={mergeProposal.review_status !== NEEDS_REVIEW_STATUS}
            laneControls={
              <LaneControls
                mergeProposal={mergeProposal}
                mergeProposalSubject={mergeProposalSubject}
                onChange={handleMergeStateChange}
              />
            }
          />
        ))}
    </>
  );
};

export default MergeProposalCleaningArea;
